import { Button, Form, Input, message, Select } from 'antd';
import { useTranslation } from '../../../i18n/i18n';
import React, { useEffect, useMemo, useState } from 'react';
import './EditInstallationLocationModal.scss';
import { InstallationLocationFormValues } from './CreateInstallationLocationModal';
import { EditOutlined } from '@ant-design/icons';
import ModalCustom from '../../utils/ModalCustom/ModalCustom';
import { userApi } from '../../../api/user.api';
import { UserRole } from '../../../gen/proto/api/gateway/v1/user_pb';
import useAuthStore from '../../../stores/auth.store';
import { tenantApi } from '../../../api/tenant.api';

export type UpdateLocationParams = {
  uuid: string;
  name: string;
  erp_id: string;
  address: string;
  cp: string;
  city: string;
  country: string;
  latitude: number;
  longitude: number;
  tenantUuid: string;
  adminUuid: string;
};

interface EditInstallationLocationModalProps {
  uuid: string;
  name: string;
  erpId: string;
  address: string;
  postalCode: string;
  city: string;
  latitude: string;
  longitude: string;
  tenantUuid: string;
  adminUuid?: string;
  adminName?: string;
  onSave: (values: UpdateLocationParams) => Promise<void>;
}

const PAGE_INDEX = 1;
const PAGE_SIZE = 100;
const MAX_LON = 180;
const MAX_LAT = 90;

const EditInstallationLocationModal = ({
  uuid,
  name,
  erpId,
  address,
  postalCode,
  city,
  latitude,
  longitude,
  tenantUuid,
  adminUuid,
  adminName,
  onSave,
}: EditInstallationLocationModalProps) => {
  const { t } = useTranslation('installation-location', { keyPrefix: 'installationLocationDetail' });
  const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
  const [form] = Form.useForm<InstallationLocationFormValues>();
  const [messageApi, contextHolder] = message.useMessage();
  const isSuperAdminOrCompanyAdmin = useAuthStore((state) => state.isSuperAdminOrCompanyAdmin);
  const isSuperAdmin = useAuthStore((state) => state.isSuperAdmin);
  const [adminOptions, setAdminOptions] = useState<{ value: string; label: string }[]>([]);
  const [tenantOptions, setTenantOptions] = useState<{ value: string; label: string }[]>([]);
  const [selectedTenantUuid, setSelectedTenantUuid] = useState<string>(tenantUuid);

  useEffect(() => {
    if (!isModalOpen || !selectedTenantUuid) return;
    setAdminOptions([]);
    let active = true;
    userApi
      .listUsersByPage(PAGE_INDEX, PAGE_SIZE)
      .then((response) => {
        if (!active) return;
        const admins = response.users
          .filter((u) => u.role === UserRole.ADMIN && u.tenantUuid === selectedTenantUuid)
          .map((u) => ({ value: u.uuid, label: u.name }));
        setAdminOptions(admins);
      })
      .catch(() => {
        if (active) setAdminOptions([]);
      });
    return () => {
      active = false;
    };
  }, [isModalOpen, selectedTenantUuid]);

  useEffect(() => {
    if (!isModalOpen || !isSuperAdmin) return;
    let active = true;
    tenantApi
      .listTenants()
      .then((tenants) => {
        if (active) setTenantOptions(tenants.map((t) => ({ value: t.uuid, label: t.name })));
      })
      .catch(() => {
        if (active) setTenantOptions([]);
      });
    return () => {
      active = false;
    };
  }, [isModalOpen, isSuperAdmin]);

  const displayedAdminOptions = useMemo(() => {
    if (!adminUuid || !adminName || selectedTenantUuid !== tenantUuid) return adminOptions;
    if (adminOptions.some((option) => option.value === adminUuid)) return adminOptions;
    return [...adminOptions, { value: adminUuid, label: adminName }];
  }, [adminOptions, adminUuid, adminName, selectedTenantUuid, tenantUuid]);

  const rangeValidator = (min: number, max: number, errorKey: string) => (_: unknown, value: string) => {
    if (!value) return Promise.resolve();
    const num = parseFloat(value);
    if (isNaN(num) || num < min || num > max) return Promise.reject(t(errorKey));
    return Promise.resolve();
  };

  const handleTenantChange = (value: string) => {
    setSelectedTenantUuid(value);
    form.setFieldValue('userAdmin', undefined);
  };

  const handleOpen = () => {
    form.setFieldsValue({
      erpId,
      address,
      postalCode,
      city,
      lat: latitude,
      lon: longitude,
      userAdmin: adminUuid ?? undefined,
      tenant: tenantUuid,
    });
    setSelectedTenantUuid(tenantUuid);
    setIsModalOpen(true);
  };

  const handleSaveButton = async () => {
    let rawValues: InstallationLocationFormValues;
    try {
      rawValues = await form.validateFields();
    } catch {
      return;
    }
    try {
      const lat = parseFloat(rawValues.lat);
      const lon = parseFloat(rawValues.lon);
      await onSave({
        uuid,
        name,
        erp_id: rawValues.erpId,
        address: rawValues.address,
        cp: rawValues.postalCode,
        city: rawValues.city,
        country: 'ES',
        latitude: lat,
        longitude: lon,
        tenantUuid: isSuperAdmin ? selectedTenantUuid : tenantUuid,
        adminUuid: rawValues.userAdmin,
      });
      setIsModalOpen(false);
    } catch (error) {
      messageApi.error(t('common:errors.generic'));
    }
  };

  return (
    <>
      {contextHolder}
      <Button onClick={handleOpen}>
        <EditOutlined />
        {t('edit')}
      </Button>
      <ModalCustom
        title={t('editInstallationLocation')}
        description={t('editDesc')}
        isModalOpen={isModalOpen}
        setIsModalOpen={setIsModalOpen}
        footer={
          <Button type={'primary'} onClick={handleSaveButton}>
            {t('save')}
          </Button>
        }
      >
        <Form
          form={form}
          layout="vertical"
          className={'Modal__form'}
          onFinish={handleSaveButton}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && !e.defaultPrevented) {
              e.preventDefault();
              form.submit();
            }
          }}
        >
          <Form.Item name="erpId" label={t('erpId')} rules={[{ required: true }]}>
            <Input />
          </Form.Item>
          <Form.Item name="address" label={t('address')} rules={[{ required: true }]}>
            <Input />
          </Form.Item>
          <Form.Item
            name="postalCode"
            label={t('postalCode')}
            rules={[{ required: true }, { pattern: /^\d+$/, message: t('errorInteger') }]}
          >
            <Input />
          </Form.Item>
          <Form.Item name="city" label={t('poblation')} rules={[{ required: true }]}>
            <Input />
          </Form.Item>
          <Form.Item
            name="lat"
            label={t('lat')}
            rules={[
              { required: true },
              { pattern: /^-?\d+(\.\d+)?$/, message: t('errorFloat') },
              { validator: rangeValidator(-MAX_LAT, MAX_LAT, 'errorLatRange') },
            ]}
          >
            <Input />
          </Form.Item>
          <Form.Item
            name="lon"
            label={t('lon')}
            rules={[
              { required: true },
              { pattern: /^-?\d+(\.\d+)?$/, message: t('errorFloat') },
              { validator: rangeValidator(-MAX_LON, MAX_LON, 'errorLonRange') },
            ]}
          >
            <Input />
          </Form.Item>
          {isSuperAdmin && (
            <Form.Item name="tenant" label={t('tenant')} rules={[{ required: true }]}>
              <Select options={tenantOptions} onChange={handleTenantChange} />
            </Form.Item>
          )}
          <Form.Item name="userAdmin" label={t('admin')} rules={[{ required: true }]}>
            <Select options={displayedAdminOptions} disabled={!isSuperAdminOrCompanyAdmin} />
          </Form.Item>
        </Form>
      </ModalCustom>
    </>
  );
};

export default EditInstallationLocationModal;
